// Dashboard: the last 30 days across all widgets (headline numbers and the
// drop-off funnel), QuoteFlow's quotes (companies with QuoteFlow only), a
// numbered list of setup tasks still to do (complete your profile, create your
// first component). The Analytics page has other periods, trends and per-widget
// figures. With both heat pump and solar on the subscription, a switch narrows
// the summary to one product (ProductSwitch).
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Plus, UserRound } from "lucide-react";
import { useApi, type BillingOverview, type CompanyProfile } from "../api";
import { QuoteFlowPanel } from "../components/QuoteFlowPanel";
import { Funnel, KpiRow, useStats } from "../components/analytics";
import { ProductSwitch, useProductFilter } from "../components/ProductSwitch";
import { useAuth } from "../auth/AuthContext";
import { Attention } from "../components/Attention";
import { PromoCarousel } from "../components/PromoCarousel";
import "../dashboard.css";

// One setup task on the dashboard's Start here list
type Task = { key: string; title: string; body: string; to: string; action: string; icon: ReactNode };

export function Dashboard() {
  const { user } = useAuth();

  // New accounts (no widgets yet) get a single "start here" prompt; null = still loading
  const api = useApi();

  // The subscription (Stripe): decides whether the product switch is offered
  const [billing, setBilling] = useState<BillingOverview | null>(null);
  useEffect(() => {
    api
      .getBilling()
      .then(setBilling)
      .catch(() => undefined);
  }, [api]);
  // All / ASHP / Solar: only offered when the subscription has both products
  const { available: showSwitch, product, setProduct } = useProductFilter(billing);

  // Fixed summary: the last 30 days across every widget (Analytics has the rest)
  const { stats, error: statsError } = useStats("30d", undefined, product);
  const [widgetCount, setWidgetCount] = useState<number | null>(null);
  // Tools that can't go live because they aren't paid for (their status says so)
  const [unpaidCount, setUnpaidCount] = useState(0);
  // Contact forms stay out of the ASHP Estimator journey (no postcode or price steps)
  const [contactIds, setContactIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    api
      .listWidgets()
      .then((w) => {
        setWidgetCount(w.length);
        setUnpaidCount(w.filter((x) => x.statusMissing.some((m) => m.key === "subscription")).length);
        setContactIds(new Set(w.filter((x) => x.branding?.layout?.style === "contact").map((x) => x.widgetId)));
      })
      .catch(() => undefined);
  }, [api]);

  // Account → Profile: for the greeting and the "complete your profile" task
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  useEffect(() => {
    api
      .getProfile()
      .then(setProfile)
      .catch(() => undefined);
  }, [api]);
  // Profile first name, else the SSO (Google) first name from the token
  const firstName = profile?.user.firstName.trim() || user?.givenName || null;
  // Greet by first name once it's set, else by email
  const greetName = firstName ?? user?.email;

  // Setup tasks still to do, in order (only shown once we know; none = no list)
  const tasks: Task[] = [];
  if (profile && (!firstName || !profile.company.companyName.trim())) {
    tasks.push({
      key: "profile",
      title: "Complete your profile",
      body: "Add your name and your company's details. They're used to greet you and to fill in your hosted pages' header and footer.",
      to: "/profile",
      action: "Complete profile",
      icon: <UserRound size={16} aria-hidden="true" />,
    });
  }
  if (unpaidCount > 0) {
    tasks.push({
      key: "billing",
      title: "Put your website components live",
      body: `${unpaidCount === 1 ? "1 component isn't" : `${unpaidCount} components aren't`} live yet because ${unpaidCount === 1 ? "it isn't" : "they aren't"} on your plan. Add ${unpaidCount === 1 ? "it" : "them"} in Billing; until then they only work on the test page.`,
      to: "/billing",
      action: "Go to Billing",
      icon: <CreditCard size={16} aria-hidden="true" />,
    });
  }
  if (widgetCount === 0) {
    tasks.push({
      key: "tool",
      title: "Create your first website component",
      body: "Style it to match your site, add your prices and where to send enquiries, then paste one line of code.",
      to: "/components/new",
      action: "Create a component",
      icon: <Plus size={16} aria-hidden="true" />,
    });
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Dashboard</h1>
        <p>Welcome back{greetName ? `, ${greetName}` : ""}. Here's how your website components are doing.</p>
      </div>

      {/* Start here: numbered setup tasks; the pulsing star marks the next one */}
      {tasks.length > 0 && (
        <section className="start-tasks" aria-label="Start here">
          <div className="start-here-label">Start here</div>
          <ol>
            {tasks.map((t, i) => (
              <li key={t.key} className="start-task">
                <div className="card start-here">
                  {/* The task number, inside the card on the left */}
                  <span className="start-task-num has-attention">
                    {i + 1}
                    {i === 0 && <Attention corner="tr" label="Do this first" />}
                  </span>
                  <div className="start-task-text">
                    <h2>{t.title}</h2>
                    <p className="card-sub">{t.body}</p>
                  </div>
                  <Link to={t.to} className={`btn ${i === 0 ? "btn-primary" : "btn-secondary"}`}>
                    {t.icon} {t.action}
                  </Link>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {statsError && <div className="flash flash-danger">{statsError}</div>}
      {/* The product switch, above the figures it filters */}
      {showSwitch && (
        <div className="dash-filters">
          <ProductSwitch value={product} onChange={setProduct} />
        </div>
      )}
      <KpiRow stats={stats} period="Last 30 days" product={product} />
      {stats && <Funnel stats={stats} period="Last 30 days" exclude={contactIds} quoteFlow={billing?.plan === "quoteflow" && product !== "solar"} product={product} />}

      {/* Quotes: only for companies with QuoteFlow */}
      <QuoteFlowPanel />

      {/* xenscope: a small rotating advertising board, full width */}
      <PromoCarousel />
    </div>
  );
}

